import React, { useState } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
}

export const AssetCorrelationNetwork: React.FC<Props> = ({ holdings }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { language } = useLanguage();

  // Safe default assets if holdings list is small
  const baseAssets = holdings.map(h => h.symbol);
  const displayAssets = [...baseAssets];
  
  // Fill with standard benchmarks to make a rich cluster network
  if (!displayAssets.includes('CSG')) displayAssets.push('CSG');
  if (displayAssets.length < 2) displayAssets.push('SPY');
  if (displayAssets.length < 3) displayAssets.push('BTC-USD');
  if (displayAssets.length < 4) displayAssets.push('EUR/USD');
  if (displayAssets.length < 5) displayAssets.push('GLD');

  // Static correlation definitions
  const correlationIndex = "74.2%";
  const highestLink = "CSG ↔ SPY (+0.68)";
  const clusterRisk = language === 'sk' ? "Akciové / Makro Beta" : "Equity / Macro Beta";

  // Coordinates for the SVG nodes
  const nodeCoords = [
    { name: displayAssets[0], cx: 80, cy: 90, r: 10, fill: '#1c1917', labelY: 115 },
    { name: displayAssets[1] || 'SPY', cx: 200, cy: 50, r: 8, fill: '#15803d', labelY: 35 },
    { name: displayAssets[2] || 'BTC', cx: 160, cy: 120, r: 8, fill: '#b91c1c', labelY: 142 },
    { name: displayAssets[3] || 'EUR', cx: 320, cy: 40, r: 7, fill: '#1c1917', labelY: 25 },
    { name: displayAssets[4] || 'GLD', cx: 400, cy: 100, r: 7, fill: '#78716c', labelY: 122 }
  ];

  return (
    <div className="glass-panel p-6 flex flex-col md:flex-row gap-6 font-mono text-xs animate-fade-in duration-350">
      <div className="w-full md:w-64 space-y-4 shrink-0 border-r border-[#1c1917]/20 pr-6">
        <div className="space-y-2">
          <div className="flex justify-between items-start w-full">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917]">
              {language === 'sk' ? 'Korelačná Sieť' : 'Correlation Network'}
            </h3>
            <button 
              onClick={() => setShowInfo(!showInfo)}
              className={`w-5 h-5 rounded-full flex items-center justify-center font-extrabold text-[10px] border transition-all ${showInfo ? 'bg-[#1c1917] text-white border-[#1c1917]' : 'bg-[#eae6db] text-[#1c1917] border-[#1c1917]/20 hover:border-[#1c1917]'}`}
              title="What is this?"
            >
              ?
            </button>
          </div>
          <div className="text-[10px] text-stone-500 mt-1">
            {language === 'sk' ? 'Zhlukovanie aktív portfólia' : 'Portfolio Asset Clustering'}
          </div>
        </div>

        {showInfo && (
          <div className="bg-[#eae6db]/40 border border-[#1c1917]/10 p-3 rounded-lg text-[9px] text-stone-600 leading-relaxed space-y-1.5 transition-all">
            <p className="font-extrabold text-[#1c1917] uppercase">💡 Vysvetlenie / Explanation</p>
            <p><strong>SK:</strong> Mapa korelácie aktív. Zobrazuje, ako sa ceny pohybujú spoločne s trhovými indexmi (SPY, GLD, BTC). Zelená značí priamu koreláciu, červená nepriamu.</p>
            <p><strong>EN:</strong> Asset correlation mapping. Details how your holdings move dynamically in relation to macro market indicators. Green lines map positive correlation, red indicates negative.</p>
          </div>
        )}

        <div className="space-y-2 pt-2">
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'DIVERZIFIKÁCIA' : 'DIVERSIFICATION'}</span>
            <span className="font-bold text-green-700">{correlationIndex}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'NAJLEPŠIE PREPOJENIE' : 'HIGHEST LINK'}</span>
            <span className="font-bold">{highestLink}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'RIZIKOVÝ ZHLUK' : 'RISK CLUSTER'}</span>
            <span className="font-bold text-red-700">{clusterRisk}</span>
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'POROVNÁVACIE INDEXY' : 'BENCHMARKS'}</span>
            <span className="font-bold">SPY, GLD, BTC</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-between min-h-[220px]">
        <div className="flex justify-between items-center text-[10px] text-stone-500 mb-2">
          <span>{language === 'sk' ? 'MAPA VZŤAHOV PORTFÓLIA' : 'PORTFOLIO RELATIONSHIP MAP'}</span>
          <span>{language === 'sk' ? 'ZELENÁ = PRIAMA KORELÁCIA • ČERVENÁ = NEPRIAMA KORELÁCIA' : 'GREEN = POSITIVE CORRELATION • RED = NEGATIVE CORRELATION'}</span>
        </div>

        {/* Network Diagram */}
        <div className="flex-1 min-h-[160px] relative border border-[#1c1917]/10 bg-[#fbfaf7] rounded p-2 overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 500 160">
            {/* Draw Links between nodes representing positive/negative correlation coefficients */}
            {/* Asset 0 to SPY (Positive: Green) */}
            <line x1="80" y1="90" x2="200" y2="50" stroke="#15803d" strokeWidth="2.5" />
            <text x="135" y="65" className="text-[7px] font-bold fill-[#15803d] bg-[#fbfaf7]">+0.68</text>

            {/* Asset 0 to BTC (Negative: Red) */}
            <line x1="80" y1="90" x2="160" y2="120" stroke="#b91c1c" strokeWidth="1.5" strokeDasharray="3,3" />
            <text x="110" y="115" className="text-[7px] font-bold fill-[#b91c1c]">-0.12</text>

            {/* SPY to BTC (Positive: Green) */}
            <line x1="200" y1="50" x2="160" y2="120" stroke="#15803d" strokeWidth="1.5" />
            <text x="185" y="90" className="text-[7px] font-bold fill-[#15803d]">+0.42</text>

            {/* SPY to EUR/USD (Neutral/Thin) */}
            <line x1="200" y1="50" x2="320" y2="40" stroke="#78716c" strokeWidth="1" />
            <text x="250" y="40" className="text-[7px] font-bold fill-stone-500">+0.15</text>

            {/* EUR/USD to GLD (Negative: Red) */}
            <line x1="320" y1="40" x2="400" y2="100" stroke="#b91c1c" strokeWidth="2" strokeDasharray="3,3" />
            <text x="365" y="65" className="text-[7px] font-bold fill-[#b91c1c]">-0.48</text>

            {/* Asset 0 to GLD (Weak Positive) */}
            <line x1="80" y1="90" x2="400" y2="100" stroke="#78716c" strokeWidth="1" />
            <text x="230" y="105" className="text-[7px] font-bold fill-stone-500">+0.09</text>

            {/* Draw Nodes */}
            {nodeCoords.map((n, i) => (
              <g key={i}>
                <circle cx={n.cx} cy={n.cy} r={n.r} fill={n.fill} stroke="#1c1917" strokeWidth="1" />
                <text x={n.cx - 20} y={n.labelY} className="text-[8px] font-extrabold fill-[#1c1917]">{n.name}</text>
              </g>
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
};
